import { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row, message } from 'antd';
import dayjs from 'dayjs';
import { RightOutlined, CheckOutlined } from '@ant-design/icons';
import InputItem from '../../components/InputItem';
import UploadItem from '../../components/UploadItem';
import SelectorItem from '../../components/SelectorItem';
import TextEditor from '../../components/TextEditor';
import EditOrganizationDrawer from './EditOrganizationDrawer';

import request from '@/request';

const steps = ['基本信息', '详细介绍', '上传附件'];

export default function EditInfo({ event = {}, editable, onComplete }) {
  const user = useSelector((state) => state.auth.user);
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orgDrawerOpen, setOrgDrawerOpen] = useState(false);
  // 仅赛事状态为比赛中或报名结束待开赛时允许修改时间
  const canEditTime = !event.id || [2, 3].includes(Number(event.status));

  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    cover: '',
    mainPic: '',
    cityName: '',
    address: '',
    registrationTime: null,
    competitionTime: [],
    contactPhone: '',
    contactEmail: '',
    describe: '',
    annex: [],
    assistUnit: [{ name: '主办单位', desc: user?.org?.name || '', isMain: true }],
  });

  // Update formData when event changes
  useEffect(() => {
    if (event.id) {
      setFormData({
        name: event.name || '',
        cover: event.cover,
        mainPic: event.mainPic || '',
        cityName: event.cityName || '',
        address: event.address || '',
        registrationTime:
          event.registrationTime || [
            dayjs(event.applyStartTime),
            dayjs(event.applyEndTime),
          ] ||
          [],
        competitionTime:
          event.competitionTime || [
            dayjs(event.startDate),
            dayjs(event.endDate),
          ] ||
          [],
        contactPhone: event.contactPhone || '',
        contactEmail: event.contactEmail || '',
        describe: event.describe || '',
        annex: event.annex || [],
        qualification: event.qualification || [],
        assistUnit: event.assistUnit?.length > 0
          ? event.assistUnit
          : [{ name: '主办单位', desc: user?.org?.name || '', isMain: true }],
      });
    }
  }, [event]);

  // Update form field
  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isNextDisabled = useCallback(() => {
    const isStep0Complete = () => {
      return (
        formData.name.trim() !== '' &&
        formData.cover !== '' &&
        formData.mainPic !== '' &&
        formData.cityName !== '' &&
        formData.address.trim() !== '' &&
        formData.competitionTime?.length > 0 &&
        formData.contactPhone.trim() !== '' &&
        formData.contactEmail.trim() !== '' &&
        (formData.assistUnit[0]?.desc || '').trim() !== ''
      );
    };
    if (step === 0) {
      return !isStep0Complete();
    }
    return false;
  }, [formData, step]);

  const handleNext = useCallback(async () => {
    try {
      const params = { ...formData };
      params.startDate = formData.competitionTime[0];
      params.endDate = formData.competitionTime[1];
      params.applyStartTime = formData.registrationTime[0];
      params.applyEndTime = formData.registrationTime[1];
      delete params.competitionTime;
      delete params.registrationTime;
      setIsSubmitting(true);
      if (step === 2) {
        if (!formData.qualification || formData.qualification.length === 0) {
          message.warning('请上传赛事资质');
          return;
        }
      }
      if (!event.id) {
        const response = await request.post('/events/v2', params);
        if (response && onComplete) {
          onComplete(response);
        }
      } else {
        await request.put(`/events/v2/${event.id}`, params);
        if (onComplete) onComplete(event.id);
      }
    } finally {
      setIsSubmitting(false);
    }
    const isLast = step >= steps.length - 1;
    if (!isLast) {
      setStep((prev) => Math.min(prev + 1, steps.length - 1));
    } else {
      message.success('保存成功');
    }
  }, [step, formData, event, onComplete]);

  return (
    <>
      <div className="mb-10 flex items-center justify-between">
        <div className="flex gap-3">
          {steps.map((title, index) => (
            <button
              key={index}
              onClick={() => setStep(index)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                step === index
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {title}
            </button>
          ))}
        </div>
        <button
          onClick={handleNext}
          disabled={isNextDisabled()}
          className={`px-6 pl-5 py-2 rounded-lg text-sm font-medium transition-colors duration-200 inline-flex items-center gap-2 ${
            isNextDisabled()
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
          }`}
        >
          {isSubmitting ? (
            <RightOutlined />
          ) : step >= steps.length - 1 ? (
            <CheckOutlined />
          ) : (
            <RightOutlined />
          )}
          <span>保存</span>
        </button>
      </div>
      {step === 0 && (
        <>
          <Row className="mb-4">
            <Col span={23}>
              <InputItem
                label="赛事名称"
                require
                value={formData.name}
                onChange={(value) => updateField('name', value)}
              />
            </Col>
          </Row>
          <Row className="mt-6">
            <Col span={11}>
              <UploadItem
                require
                label="赛事头图"
                placeholder="尺寸为500*500，支持JPG, JPEG, PNG格式的图片，最大不超过3M"
                value={formData.cover}
                onChange={(url) => updateField('cover', url)}
                demoUrl="https://statics.dronex.cc/template/cover_example.jpg"
              />
            </Col>
            <Col span={11} offset={1}>
              <UploadItem
                require
                label="赛事主图"
                placeholder="尺寸为750*500，支持JPG, JPEG, PNG格式的图片，最大不超过3M"
                value={formData.mainPic}
                onChange={(url) => updateField('mainPic', url)}
                demoUrl="https://statics.dronex.cc/template/main_example.jpg"
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Col span={11}>
              <SelectorItem
                label="比赛城市"
                type="address"
                require
                value={formData.cityName}
                onChange={(value) => updateField('cityName', value)}
              />
            </Col>
            <Col span={11} offset={1}>
              <InputItem
                label="比赛地址"
                require
                value={formData.address}
                onChange={(value) => updateField('address', value)}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Col span={11}>
              <SelectorItem
                label="报名时间"
                type="time"
                disabled={!canEditTime}
                value={formData.registrationTime}
                onChange={(value) => updateField('registrationTime', value)}
                require
              />
            </Col>
            <Col span={11} offset={1}>
              <SelectorItem
                label="比赛时间"
                type="time"
                require
                disabled={!canEditTime}
                value={formData.competitionTime}
                onChange={(value) => updateField('competitionTime', value)}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Col span={11}>
              <InputItem
                label="联系电话"
                require
                value={formData.contactPhone}
                onChange={(value) => updateField('contactPhone', value)}
                maxLength={11}
              />
            </Col>
            <Col span={11} offset={1}>
              <InputItem
                label="联系邮箱"
                require
                value={formData.contactEmail}
                onChange={(value) => updateField('contactEmail', value)}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            <Col span={11}>
              <button
                type="button"
                onClick={() => setOrgDrawerOpen(true)}
                className="w-full border-2 border-dashed border-blue-200 rounded-md h-10 text-blue-600 text-sm font-medium hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                管理支持单位（{formData.assistUnit.length} 个）
              </button>
            </Col>
          </Row>
          <EditOrganizationDrawer
            open={orgDrawerOpen}
            onClose={() => setOrgDrawerOpen(false)}
            value={formData.assistUnit}
            onChange={(data) => updateField('assistUnit', data)}
            eventId={event.id}
          />
        </>
      )}
      {step === 1 && (
        <div className="flex justify-center">
          <TextEditor
            value={formData.describe}
            onChange={(value) => updateField('describe', value)}
          />
        </div>
      )}
      {step === 2 && (
        <>
          <UploadItem
            multiple
            require
            label="上传赛事资质"
            value={formData.qualification}
            onChange={(files) => updateField('qualification', files)}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            placeholder="请点击或拖拽文件至改区域进行上传"
            demoDownloadUrl="https://statics.dronex.cc/template/%E8%B5%9B%E4%BA%8B%E5%AE%A1%E6%A0%B8%E6%9D%90%E6%96%99%E7%A4%BA%E4%BE%8B.zip"
          />
          <UploadItem
            multiple
            label="上传赛事附件"
            value={formData.annex}
            onChange={(files) => updateField('annex', files)}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            placeholder="请点击或拖拽文件至改区域进行上传"
          />
        </>
      )}
    </>
  );
}
